import React from 'react';

import { Hidden } from '@material-ui/core';

import { IReport, IReportEntity } from '../../../../state/types';
import {
    TPreviewPane,
    activateChart,
    activateGrid,
    handleExpandReportUpdate,
} from '../../../../state/ui/discovery/snapshotting';

import { makeSourceSetId } from '../../../../services/discovery/_shared/utils';
import { makeSourceSetUrl } from '../../../../services/discovery/_shared/urlMakers';

import { useAppDispatch } from '../../../../hooks';
import { ISourceSetAction } from '../../../../state/ui/discovery/types';

import useOpenSourceSet from '../../hooks/useOpenSourceSet';
import { useEntityAction, useReportAction } from '../../hooks/useHandleActions';

import {
    useCreatorLevel,
    useExtendedReports,
    usePreviewAction,
    useTimeLocked,
} from '../../selectors/index.hooks';

import Report from '../../../../components/report/Report';
import ReportList from '../../../../components/report/ReportList/ReportList';
import ReportSourceSetActionsIcons from './components/ReportSourceSetActionsIcons';
import ReportListItem from '../../../../components/report/ReportListItem';
import { SourceSetAction } from '../../../../components/ButtonActions/ButtonActions';
import { useStyles } from './Themable.hooks';
import { renderImageWithActionsComponent } from '../../../../components/report/ReportListItem/utils/renderImageComponent';
import { showLiveViewDialog } from '../../../../state/ui/dialog';

interface ISourceSetEntityProps {
    action?: ISourceSetAction;
}

interface IOwnProps {
    report: IReport;
    preview?: TPreviewPane;
    afterItemDelete?: () => void;
}

const SourceSetEntity = ({ action }: ISourceSetEntityProps) => {
    const classes = useStyles();
    const panePreviewAction = usePreviewAction();
    const handleOpenSourceSetClick = useOpenSourceSet();
    const handleOnClick = () => handleOpenSourceSetClick(action);

    return (
        <>
            {action && !panePreviewAction?._webx?.changeSourceSetDisabled && (
                <Hidden smDown={true}>
                    <SourceSetAction
                        classes={classes}
                        handleOnClick={handleOnClick}
                    />
                </Hidden>
            )}
        </>
    );
};

const SourceSetReport = ({ preview, report, afterItemDelete }: IOwnProps) => {
    const dispatch = useAppDispatch();
    const classes = useStyles();
    const expandedReports = useExtendedReports();
    const timeLocked = useTimeLocked();
    const creatorLevel = useCreatorLevel();
    const handleReportAction = useReportAction(report);
    const handleEntityAction = useEntityAction(report);

    const handleOpenLiveViewClick = () => {
        const action = report?._meta?.actions?.liveView;
        if (action) {
            dispatch(
                showLiveViewDialog({
                    handleChange: () => {},
                    monitoredId: action.params.monitoredId.toString(),
                })
            );
        }
    };

    const handleOpenChartClick = () => {
        const action = report?._meta?.actions?.chart;

        if (action) {
            dispatch(
                activateChart(
                    makeSourceSetId(action.params)!,
                    action,
                    {
                        type: 'source-set-chart',
                        level: creatorLevel + 1,
                    },
                    timeLocked
                )
            );
        }
    };

    const handleOpenGridChartClick = () => {
        const action = report?._meta?.actions?.sourceSet;
        if (action) {
            dispatch(
                activateGrid(
                    makeSourceSetId(action.params) ||
                        makeSourceSetUrl(action).toString(),
                    {
                        type: 'source-set-grid-chart',
                        level: creatorLevel + 1,
                    },
                    preview ? preview : null,
                    false,
                    action,
                    timeLocked
                )
            );
        }
    };

    const renderListItem = (entity: IReportEntity, openDeletionDialog: any) => {
        const reportActionTypes = report?._meta?.actions;

        const { add: addAction, sourceSet: sourceSetAction } =
            entity.actions ?? {};

        const editHandler = reportActionTypes?.edit
            ? handleReportAction
            : handleEntityAction;
        const previewHandler = reportActionTypes?.preview
            ? handleReportAction
            : handleEntityAction;
        const addHandler = addAction
            ? handleEntityAction
            : reportActionTypes?.add && handleReportAction;
        return (
            <ReportListItem
                report={report}
                entity={entity}
                customRender={<SourceSetEntity action={sourceSetAction} />}
                handleDelete={openDeletionDialog}
                editHandler={editHandler}
                addHandler={addHandler}
                previewHandler={previewHandler}
                renderImageComponent={(previewUrl, downloadUrl) =>
                    renderImageWithActionsComponent(
                        entity,
                        previewUrl,
                        downloadUrl,
                        classes,
                        report.id
                    )
                }
            />
        );
    };
    return (
        <>
            <Report
                report={report}
                expandedReports={expandedReports}
                afterItemDelete={afterItemDelete}
                onExpand={() =>
                    dispatch(handleExpandReportUpdate(report.label))
                }
                renderReportList={({
                    open,
                    handleExpandClick,
                    openDeletionDialog,
                }) => (
                    <ReportList
                        open={open}
                        handleExpandClick={handleExpandClick}
                        report={report}
                        openDeletionDialog={openDeletionDialog}
                        renderListItem={renderListItem}
                        addActionHandler={() => handleReportAction('add')}
                        additionalActions={
                            <ReportSourceSetActionsIcons
                                report={report}
                                handleOpenLiveViewClick={
                                    handleOpenLiveViewClick
                                }
                                handleOpenChartClick={handleOpenChartClick}
                                handleOpenGridChartClick={
                                    handleOpenGridChartClick
                                }
                            />
                        }
                    />
                )}
            />
        </>
    );
};

export default SourceSetReport;
